import { useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, Coins } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function ReviewDialog({ open, onClose }) {
  const { updateCredits } = useAuth();
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!stars) {
      toast.error("Pick a star rating first.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/reviews", { stars, comment });
      updateCredits(data.credits);
      toast.success(data.rewarded ? "Thank you! 50 credits were added to your account 🎉" : "Review updated — thank you!");
      onClose(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !sending && !o && onClose(false)}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-400" fill="currentColor" /> Give Videoileo a review
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-400 text-left">
            Rate us and drop a short line — it helps other creators find Videoileo, and we'll thank you with credits.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 text-xs font-semibold" data-testid="review-reward-badge">
            <Coins className="h-3.5 w-3.5" /> +50 credits for your first review
          </div>
          <div className="flex items-center gap-1.5" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setStars(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                data-testid={`review-star-${n}`}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`h-7 w-7 transition-colors ${(hover || stars) >= n ? "text-amber-400" : "text-zinc-700"}`}
                  fill={(hover || stars) >= n ? "currentColor" : "none"}
                />
              </button>
            ))}
            {stars > 0 && <span className="ml-2 text-sm text-zinc-400">{stars}/5</span>}
          </div>
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={300}
            placeholder="One line on what you like (optional)"
            data-testid="review-comment-input"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={() => onClose(false)}
              disabled={sending}
              data-testid="review-later-btn"
              className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
            >
              Maybe later
            </button>
            <button
              onClick={submit}
              disabled={sending}
              data-testid="review-submit-btn"
              className="flex-1 bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Submit review"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
